import { useState, useRef, useContext } from 'react';
import { useForm, Controller } from 'react-hook-form';
import isDate from 'date-fns/isValid';
import dateFormat from 'date-fns/format';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import UploadIcon from '@material-ui/icons/CloudUpload';
import WarningIcon from '@material-ui/icons/Warning';
import Modal from 'components/CncUI/Modal';
import Typography from 'components/CncUI/Typography';
import Radio from 'components/CncUI/Radio';
import TextField from 'components/CncUI/TextField';
import TextFieldSsn from 'components/CncUI/TextField/TextFieldSsn';
import TextFieldSin from 'components/CncUI/TextField/TextFieldSin';
import DatePicker from 'components/CncUI/DatePicker';
import Dropdown from 'components/CncUI/Dropdown';
import Button from 'components/CncUI/Button';
import Checkbox from 'components/CncUI/Checkbox';
import Alert from 'components/CncUI/Alert';
import Loader from 'components/Loader';
import SnackbarContext from 'context/Snackbar/Context';

import { Employers, TaxDocumentTypes } from 'constants/taxDocuments';
import valid from 'utilities/valid';
import Logger from 'utilities/logger';

import withPermissionProtection from 'hoc/withPermissionProtection';
import useQuery from 'hooks/useQuery';
import useMutation from 'hooks/useMutation';

const useStyles = makeStyles(({ palette, breakpoints, spacing }) => ({
  header: {
    marginTop: 40,
    [breakpoints.up('lg')]: {
      fontSize: 48,
      lineHeight: '56px',
    },
  },
  form: {
    '& .cncui-textfield': {
      marginBottom: spacing(1),
    },
  },
  action: {
    '& .MuiButton-root .MuiSvgIcon-root': {
      marginRight: spacing(1),
      fontSize: 18,
    },
  },
  hidden: {
    display: 'none',
  },
  danger: {
    color: palette.cncUI.alert.red.primary,
  },
  fileError: {
    color: palette.cncUI.alert.red.primary,
    margin: 0,
  },
  loadingModal: {
    paddingTop: '68px !important',
    paddingBottom: 68,
  },
}));

const TaxDocumentsUpload = () => {
  const logger = Logger('taxDocumentUpload');
  const classes = useStyles();
  const uploadRef = useRef();
  const timer = useRef({ timeout: null, waiting: false });
  const notify = useContext(SnackbarContext);
  const createTaxDocument = useMutation('CreateTaxDocument', {
    scopes: 'tax_documents:create',
    request: data => data,
    response: res => res.data.createTaxDocument,
  });
  const removeTaxDocument = useMutation('removeTaxDocument', {
    scopes: 'tax_documents:edit',
    request: id => ({ id }),
    response: res => res.data.removeTaxDocument,
  });
  const getTaxDocumentStatus = useQuery('getTaxDocumentStatus', {
    fetchPolicy: 'network-only',
    scopes: 'tax_documents:edit',
    request: id => ({ id }),
    response: res => res.data.getTaxDocumentStatus,
  });
  const [idType, setIdType] = useState('ssn');
  const [modal, showModal] = useState('');
  const [state, setState] = useState({
    file: null,
    fileName: null,
    loading: false,
    error: '',
  });
  const modify = data => setState(old => ({ ...old, ...data }));

  const {
    register,
    errors,
    control,
    handleSubmit,
    watch,
    reset: resetForm,
    formState,
  } = useForm({
    mode: 'all',
    defaultValues: {
      firstName: '',
      lastName: '',
      employerName: '',
      birthDate: null,
      // Document type
      name: '',
      year: '',
      ssn: '',
      sendNotification: false,
    },
  });

  const nextDisabled = !formState.isValid;
  const docType = watch('name');
  const idNumber = watch('ssn');
  const idNumberValidator = idType === 'ssn' ? valid.ssn : valid.sin;

  const fieldRequired = {
    required: 'This field is required',
  };

  const validIdNumber = {
    validate: value => idNumberValidator(value) || 'ID number is not valid',
  };

  const validDate = {
    validate: value => isDate(value) || 'Birth date value is not valid',
  };

  const validYear = {
    min: {
      value: 1900,
      message: 'Year value is not valid',
    },
  };

  const validFileName = () => {
    return (
      idNumber &&
      idNumberValidator(idNumber) &&
      (state.fileName || '').startsWith(idNumber)
    );
  };

  const clear = () => {
    uploadRef.current.value = null;
    modify({ error: '', file: null, fileName: null });
  };

  const onFile = e => {
    const { target: { validity, files: [file] = [] } = {} } = e || {};
    if (!validity.valid) return clear();
    try {
      const reader = new FileReader();
      reader.onload = () =>
        modify({ file: reader.result, fileName: file.name });
      reader.onerror = err => {
        clear();
        modify({ error: err.message });
      };
      reader.readAsDataURL(file);
    } catch {
      clear();
    }
  };

  const reset = () => {
    modify({
      file: null,
      fileName: null,
    });
    setIdType('ssn');
    resetForm();
  };

  const stop = () => {
    if (timer.current.timeout) {
      clearTimeout(timer.current.timeout);
    }
    timer.current = { timeout: null, waiting: false };
    modify({ loading: false });
  };

  const checkStatus = id => {
    getTaxDocumentStatus(id)
      .then(status => {
        if (!timer.current.waiting && status === 'pending') {
          removeTaxDocument(id)
            .then(() => {
              showModal('wrong');
              stop();
            })
            .catch(err => logger.error(err));
        } else if (status === 'pending') {
          setTimeout(() => checkStatus(id), 5000);
        } else {
          reset();
          if (status === 'active') {
            notify({
              type: 'success',
              title: 'Success',
              message: 'Tax document was uploaded and submitted successfully.',
            });
            showModal('');
          } else {
            showModal('infected');
          }
          stop();
        }
      })
      .catch(err => logger.error(err));
  };

  const submit = form => {
    if (!validFileName()) {
      notify({
        type: 'error',
        message: `Uploaded file name must start with ${idType &&
          idType.toUpperCase()} and match form value`,
      });
      return;
    }
    const taxDocument = {
      idType,
      ...form,
      year: parseInt(form.year, 10),
      birthDate: dateFormat(form.birthDate, 'MM/dd/yyyy'),
      file: state.file,
      sendNotification: form.type === 'W2C' && form.notification,
    };
    modify({ loading: true });
    timer.current.waiting = true;
    createTaxDocument(taxDocument)
      .then(documentId => {
        showModal('loading');
        checkStatus(documentId);
        timer.current.timeout = setTimeout(
          () => (timer.current.waiting = false),
          60000,
        );
      })
      .catch(err => {
        const { message } = err;
        const error = message.startsWith('GraphQL error:')
          ? message.substring(15)
          : message;
        logger.error(`ERROR: ${error}`, err);
        timer.current.waiting = false;
        modify({ loading: false });
        notify({
          type: 'error',
          title: 'Error ocurred',
          message: error,
        });
      });
  };

  const retryAction = () => {
    clear();
    showModal('');
  };

  const createOptions = t => ({ label: t, value: t });
  const documentTypes = TaxDocumentTypes.map(createOptions);
  const employers = Employers.map(createOptions);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} id="HeaderTitle">
        <Typography variant="h2" className={classes.header}>
          Tax Document Upload Form
        </Typography>
      </Grid>
      <Grid item xs={6} className={classes.form}>
        <form onSubmit={handleSubmit(submit)}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                light
                fullWidth
                id="documentFirstName"
                placeholder="First Name"
                label="First Name"
                name="firstName"
                inputRef={register(fieldRequired)}
                error={!!errors.firstName}
                helperText={errors.firstName && errors.firstName.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                light
                fullWidth
                id="documentLastName"
                name="lastName"
                placeholder="Last Name"
                label="Last Name"
                inputRef={register(fieldRequired)}
                error={!!errors.lastName}
                helperText={errors.lastName && errors.lastName.message}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                render={props => (
                  <Dropdown
                    light
                    fullWidth
                    id="documentType"
                    name="name"
                    placeholder="Document Type"
                    label="Document Type"
                    value={props.value}
                    data={documentTypes}
                    onChange={props.onChange}
                    inputRef={props.ref}
                    error={!!errors.name}
                    helperText={errors.name && errors.name.message}
                    className={classes.dropdown}
                  />
                )}
                name="name"
                control={control}
                rules={fieldRequired}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                light
                fullWidth
                id="documentYear"
                placeholder="Tax Document Year"
                label="Tax Document Year"
                name="year"
                inputRef={register({ ...fieldRequired, ...validYear })}
                error={!!errors.year}
                helperText={errors.year && errors.year.message}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                render={props => (
                  <Dropdown
                    light
                    fullWidth
                    id="documentEmployer"
                    name="employerName"
                    placeholder="Employer Name"
                    label="Employer Name"
                    data={employers}
                    value={props.value}
                    onChange={props.onChange}
                    inputRef={props.ref}
                    error={!!errors.employerName}
                    helperText={
                      errors.employerName && errors.employerName.message
                    }
                    className={classes.dropdown}
                  />
                )}
                name="employerName"
                control={control}
                rules={fieldRequired}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography>Identification Number</Typography>
              <Radio.Group
                id="idType"
                name="idType"
                value={idType}
                onChange={e => setIdType(e.target.value)}
              >
                <Grid container spacing={2}>
                  <Grid item>
                    <Radio value="ssn" id="idType-SSN">
                      American SSN
                    </Radio>
                  </Grid>
                  <Grid item>
                    <Radio value="sin" id="idType-SIN">
                      Canadian SIN
                    </Radio>
                  </Grid>
                </Grid>
              </Radio.Group>
            </Grid>

            <Grid item xs={12}>
              <Controller
                render={props =>
                  idType === 'ssn' ? (
                    <TextFieldSsn
                      id="documentSsn"
                      label="Social Security Number"
                      placeholder="___-__-_____ "
                      toggle
                      light
                      value={props.value}
                      onChange={e => {
                        props.onChange(e.target.value);
                      }}
                      onBlur={props.onBlur}
                      error={!!errors.ssn}
                      helperText={errors.ssn && errors.ssn.message}
                      fullWidth
                    />
                  ) : (
                    <TextFieldSin
                      id="documentSsn"
                      label="Social Insurance Number"
                      placeholder="___-___-___"
                      toggle
                      light
                      value={props.value}
                      onChange={e => {
                        props.onChange(e.target.value);
                      }}
                      onBlur={props.onBlur}
                      error={!!errors.ssn}
                      helperText={errors.ssn && errors.ssn.message}
                      fullWidth
                    />
                  )
                }
                name="ssn"
                control={control}
                rules={{ ...fieldRequired, ...validIdNumber }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                render={props => (
                  <DatePicker
                    light
                    fullWidth
                    id="documentBirth"
                    label="Date of Birth"
                    placeholder="mm / dd / yyyy"
                    value={props.value}
                    onChange={value => {
                      props.onChange(value);
                    }}
                    onBlur={props.onBlur}
                    error={!!errors.birthDate}
                    helperText={errors.birthDate && errors.birthDate.message}
                  />
                )}
                name="birthDate"
                control={control}
                rules={{ ...fieldRequired, ...validDate }}
              />
            </Grid>
            {docType === 'W2C' && (
              <Grid item xs={12}>
                <Controller
                  render={props => (
                    <Checkbox
                      id="SendNotification"
                      checked={props.value}
                      onChange={e => props.onChange(e.target.checked)}
                    >
                      Send Notification
                    </Checkbox>
                  )}
                  name="sendNotification"
                  control={control}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography style={{ marginBottom: -8, marginTop: 0 }}>
                    Upload Document
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <input
                    accept=".pdf"
                    className={classes.hidden}
                    id="FileUpload"
                    type="file"
                    onChange={onFile}
                    disabled={!idNumber}
                    ref={uploadRef}
                  />
                  <label htmlFor="FileUpload">
                    <Button
                      id="UploadButton"
                      component="span"
                      secondary
                      fullWidth
                      disabled={!idNumber}
                      startIcon={<UploadIcon />}
                    >
                      Select Document
                    </Button>
                  </label>
                </Grid>
                <Grid item xs={8}>
                  {state.fileName && (
                    <TextField
                      id="documentFilename"
                      name="documentFilename"
                      light
                      fullWidth
                      value={state.fileName}
                      onChange={() => {}}
                      readOnly
                      onClear={clear}
                      inputRef={register(fieldRequired)}
                    />
                  )}
                </Grid>
                {state.fileName && !validFileName() && (
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      className={classes.fileError}
                      id="fileError"
                    >
                      {`Uploaded file name must start with ${idType.toUpperCase()} and match form value`}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container justify="flex-end">
                <Grid item>
                  <Button
                    id="SubmitButton"
                    type="submit"
                    loading={state.loading}
                    disabled={nextDisabled || !validFileName()}
                  >
                    Submit
                  </Button>
                </Grid>
                {state.error && (
                  <Grid item xs={12}>
                    <Grid container spacing={0} justify="center">
                      <Grid item>
                        <Alert error>{state.error}</Alert>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Grid>
      <Modal open={modal === 'loading'} minWidth={450}>
        <Modal.Content className={classes.loadingModal}>
          <Grid container>
            <Grid item xs={12}>
              <Typography variant="h5" align="center" noMargin>
                The uploaded document is being scanned for viruses
              </Typography>
              <Loader
                message="This should take less than a minute"
                messageProps={{
                  variant: 'body2',
                  size: 'short',
                  noMargin: true,
                }}
              />
            </Grid>
          </Grid>
        </Modal.Content>
      </Modal>
      <Modal open={modal === 'infected'} maxWidth={450}>
        <Modal.Title warning onClose={() => showModal('')}>
          <WarningIcon /> Upload failed
        </Modal.Title>
        <Modal.Content>
          <Typography variant="body1" size="long">
            We are unable to upload the document because a threat was detected.
            Ensure the file is free from viruses and try again.
          </Typography>
        </Modal.Content>
        <Modal.Actions>
          <Grid container justify="flex-end">
            <Grid item>
              <Button onClick={() => showModal('')}>Okay</Button>
            </Grid>
          </Grid>
        </Modal.Actions>
      </Modal>
      <Modal open={modal === 'wrong'} minWidth={400}>
        <Modal.Title onClose={retryAction}>Something went wrong</Modal.Title>
        <Modal.Content>
          <Typography variant="body1" size="long">
            Please re-add the document and try submitting again.
          </Typography>
        </Modal.Content>
        <Modal.Actions>
          <Grid container justify="flex-end">
            <Grid item>
              <Button onClick={retryAction}>Retry</Button>
            </Grid>
          </Grid>
        </Modal.Actions>
      </Modal>
    </Grid>
  );
};

export default withPermissionProtection('tax_documents:create')(
  TaxDocumentsUpload,
);
